import Route from '@ember/routing/route';
import { getOwner } from '@ember/owner';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { resolvePartnerPersonas } from 'tio-employee/utils/partner-dashboard/personas';
import {
  loadAttention,
  widgetsFor,
  type AttentionResult,
  type AttentionSource,
  type DashboardContext,
  type RegisteredWidget,
} from 'tio-employee/utils/partner-dashboard/registry';
import {
  PARTNER_DASHBOARD_ATTENTION_SOURCES,
  PARTNER_DASHBOARD_WIDGETS,
} from 'tio-employee/utils/partner-dashboard/widgets';
import type SessionContextService from 'tio-employee/services/session-context';
import type PartnerService from 'tio-employee/services/partner';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type RouterService from '@ember/routing/router-service';

/** One visible widget and its in-flight data, resolved by the widget frame. */
export interface LoadedWidget {
  definition: RegisteredWidget;
  data: Promise<unknown>;
}

export interface PartnerDashboardModel {
  context: DashboardContext;
  widgets: LoadedWidget[];
  /** The merged, deadline-sorted Needs Your Attention list; never rejects. */
  attention: Promise<AttentionResult>;
}

export default class AuthenticatedAdminIndexRoute extends Route {
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare router: RouterService;
  @service declare betaExperience: BetaExperienceService;

  #context: DashboardContext | undefined;

  /** The widgets this route can show; tests swap in their own list. */
  registry: readonly RegisteredWidget[] = PARTNER_DASHBOARD_WIDGETS;

  /** The attention sources this route can load; tests swap in their own list. */
  attentionSources: readonly AttentionSource[] = PARTNER_DASHBOARD_ATTENTION_SOURCES;

  async beforeModel() {
    if (!this.sessionContext.isLoaded) {
      try {
        await this.sessionContext.load();
      } catch (e) {
        console.error('Could not load session context:', e);
      }
    }
    // Personas, not the legacy role claims behind isParticipantOnly, decide who gets the dashboard.
    this.#context = this.#dashboardContext();
    if (this.#context) return;

    if (this.sessionContext.isParticipantOnly) {
      this.router.transitionTo('authenticated.dashboard');
      return;
    }
    if (this.sessionContext.isTasRole || this.partner.isTaOnly) {
      this.router.transitionTo('authenticated.admin.tuition-assistance.index');
      return;
    }
    if (this.sessionContext.isPslfAdmin) {
      this.router.transitionTo('authenticated.admin.pslf.forms');
      return;
    }
    this.router.transitionTo('authenticated.admin.reports.index');
  }

  /**
   * Starts every visible widget's load and the attention list without awaiting
   * them, so the page paints first and each part resolves on its own.
   */
  model(): PartnerDashboardModel | undefined {
    const context = this.#context;
    if (!context) return undefined;

    const widgets = widgetsFor(this.registry, context).map((definition) => {
      // A load that throws synchronously still lands in its widget's error state.
      const data = Promise.resolve().then(() => definition.load(context));
      // Observed now so a rejection before the frame renders isn't reported as unhandled; the frame still sees it.
      data.catch(() => {});
      return { definition, data };
    });
    const attention = loadAttention(this.attentionSources, context);
    // loadAttention isn't expected to reject; if it ever does, the page's error state shows it, not an unhandled rejection.
    attention.catch(() => {});
    return { context, widgets, attention };
  }

  /** The dashboard context when beta is on and the viewer holds a partner persona; otherwise undefined. */
  #dashboardContext(): DashboardContext | undefined {
    if (!this.betaExperience.isEnabled) return undefined;

    const companyId = this.sessionContext.currentCompany?.id ?? '';
    const personas = resolvePartnerPersonas(
      this.sessionContext.currentEmployee?.jurisdictiveRelationships,
      companyId
    );
    if (!personas.size) return undefined;
    return {
      personas,
      companyId,
      employeeId: this.sessionContext.currentEmployee?.id ?? '',
      userId: this.sessionContext.user?.id ?? '',
      owner: getOwner(this) as Owner,
    };
  }
}
